import type { FC, ReactNode } from 'react'
import type { GalleryItem } from '@/types'

import Image from 'next/image'
import { Expand, MapPin } from 'lucide-react'
import { cn } from '@/lib/utils'

interface GalleryTileProps {
    item: GalleryItem
    onOpen: () => void
    sizes: string
    className?: string
    preload?: boolean
}

export const GalleryTile: FC<GalleryTileProps> = ({
    item,
    onOpen,
    sizes,
    className,
    preload = false
}): ReactNode => {
    return (
        <figure className={cn('group relative overflow-hidden rounded-2xl bg-night-soft', className)}>
            <Image
                src={item.image}
                alt={item.imageAlt || item.title}
                fill
                sizes={sizes}
                preload={preload}
                className='object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]'
            />

            <div aria-hidden='true' className='pointer-events-none absolute inset-0 bg-linear-to-t from-night/90 via-night/10 to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100' />

            <figcaption className='pointer-events-none absolute inset-x-0 bottom-0 p-4 text-white sm:p-5'>
                {item.categoryName && item.categoryId && (
                    <p className='text-[0.65rem] font-bold tracking-[0.22em] text-gold-soft uppercase'>
                        <span data-mdlfy-cms={`gallery_categories:${item.categoryId}:name`}>{item.categoryName}</span>
                    </p>
                )}

                <p className='mt-1 font-heading text-xl leading-tight sm:text-2xl'>
                    <span data-mdlfy-cms={`gallery_items:${item.id}:title`}>{item.title}</span>
                </p>

                {item.location && (
                    <p className='mt-1 flex items-center gap-1 text-xs text-white/70'>
                        <MapPin className='size-3 shrink-0' aria-hidden='true' />
                        <span data-mdlfy-cms={`gallery_items:${item.id}:location`}>{item.location}</span>
                    </p>
                )}
            </figcaption>

            <button
                type='button'
                onClick={onOpen}
                aria-label={`Open photo: ${item.title}`}
                className='focus-ring absolute inset-0 z-10 cursor-zoom-in rounded-2xl'
            >
                <span className='absolute top-3 right-3 inline-flex size-9 items-center justify-center rounded-full bg-night/50 text-white opacity-0 backdrop-blur transition-opacity duration-300 group-hover:opacity-100'>
                    <Expand className='size-4' aria-hidden='true' />
                </span>
            </button>
        </figure>
    )
}
